import { useEffect, useMemo, useRef, useState } from "react";

import type { Cluster, Finding } from "../api/types";

/** Node of the drawn graph. Clusters are hubs, entries hang off them. */
interface Node {
  id: string;
  label: string;
  kind: "cluster" | "entry";
  cluster: string;
  radius: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  score: number;
  /** Depth, 0 (far) to 1 (near). Drives size, brightness and draw order. */
  depth: number;
}

const WIDTH = 1200;
const HEIGHT = 820;
/** Enough passes to settle, few enough to compute in one frame budget. */
const ITERATIONS = 320;
/** How far the plane is tilted away from the viewer. Pure projection — the
 *  layout stays two-dimensional, only the drawing pretends otherwise. */
const TILT = 0.62;
const UNASSIGNED = "ohne";

/** Characters per line inside a theme card. Wrapping beats truncating: the
 *  whole heading has to be readable without zooming in first. */
const HUB_LINE = 20;
const HUB_LINE_HEIGHT = 15;
/** Paper-length for the hover label: long enough to keep a title in two or
 *  three lines, short enough that the eye finds the next line. */
const TIP_LINE = 44;
const TIP_LINE_HEIGHT = 19;

function wrap(label: string, perLine = HUB_LINE, maxLines = 4): string[] {
  const lines: string[] = [];
  let current = "";
  for (const word of label.split(/\s+/)) {
    if (!current) {
      current = word;
    } else if (`${current} ${word}`.length <= perLine) {
      current += ` ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  if (lines.length <= maxLines) return lines;
  // Whatever does not fit is marked as missing rather than dropped silently.
  const kept = lines.slice(0, maxLines);
  kept[maxLines - 1] = `${kept[maxLines - 1]!.slice(0, perLine - 1)}…`;
  return kept;
}

/** Half the card, in layout units — what the layout has to keep clear. */
function hubBox(node: Node): { halfWidth: number; halfHeight: number } {
  const lines = wrap(node.label);
  const longest = Math.max(...lines.map((line) => line.length), 8);
  return {
    halfWidth: longest * 3.5 + 14,
    halfHeight: (lines.length * HUB_LINE_HEIGHT) / 2 + 9,
  };
}

/** A deterministic hue per cluster: the same theme keeps its colour across
 *  reloads, which is what makes the picture readable a second time. */
function hue(slug: string): number {
  let sum = 0;
  for (const char of slug || UNASSIGNED) sum = (sum * 31 + char.charCodeAt(0)) % 360;
  return sum;
}

/** Where each node stood the last time, so a redraw is not a reshuffle. */
type Placed = Map<string, { x: number; y: number }>;

/**
 * A seat for a heading that was not on the plane a moment ago.
 *
 * Its place used to come from its index on a ring, which is fine when the
 * whole picture is built at once and wrong when one card is added to a
 * standing one: the ring position is quite likely already taken. This looks
 * for the emptiest spot instead — the candidate whose nearest neighbour is
 * furthest away.
 */
function freeSeat(taken: { x: number; y: number }[]): { x: number; y: number } {
  if (!taken.length) return { x: WIDTH / 2, y: HEIGHT / 2 - 240 };

  let best = { x: WIDTH / 2, y: HEIGHT / 2 - 240 };
  let bestDistance = -1;
  for (const radius of [250, 320, 190, 380]) {
    for (let step = 0; step < 24; step += 1) {
      const angle = (step / 24) * Math.PI * 2;
      const candidate = {
        x: WIDTH / 2 + Math.cos(angle) * radius,
        y: HEIGHT / 2 + Math.sin(angle) * radius * 0.9,
      };
      let nearest = Infinity;
      for (const seat of taken) {
        nearest = Math.min(nearest, Math.hypot(seat.x - candidate.x, seat.y - candidate.y));
      }
      if (nearest > bestDistance) {
        bestDistance = nearest;
        best = candidate;
      }
    }
  }
  return best;
}

function layout(
  findings: Finding[],
  clusters: Cluster[],
  placed: Placed,
): { nodes: Node[]; edges: [string, string][] } {
  // Empty ones too: a theme the assistant just created has to appear
  // before its first entry does, or the map looks like nothing happened.
  const present = clusters;
  const nodes: Node[] = [];
  const edges: [string, string][] = [];

  // Seats already taken, so a new heading can avoid them.
  const taken = present
    .map((cluster) => placed.get(`c:${cluster.slug}`))
    .filter((seat): seat is { x: number; y: number } => Boolean(seat));

  present.forEach((cluster, index) => {
    const angle = (index / Math.max(present.length, 1)) * Math.PI * 2;
    const spread = Math.min(WIDTH, HEIGHT) * 0.32;
    let seat = placed.get(`c:${cluster.slug}`);
    if (!seat) {
      seat = taken.length
        ? freeSeat(taken)
        : { x: WIDTH / 2 + Math.cos(angle) * spread, y: HEIGHT / 2 + Math.sin(angle) * spread };
      taken.push(seat);
    }
    nodes.push({
      id: `c:${cluster.slug}`,
      label: cluster.name,
      kind: "cluster",
      cluster: cluster.slug,
      // Filled in from the wrapped heading once the node exists.
      radius: 0,
      x: seat.x,
      y: seat.y,
      vx: 0,
      vy: 0,
      score: cluster.count,
      depth: 0.5,
    });
  });

  for (const node of nodes) node.radius = hubBox(node).halfWidth;

  const known = new Set(present.map((cluster) => cluster.slug));
  findings.forEach((finding, index) => {
    const slug =
      finding.cluster_slug && known.has(finding.cluster_slug) ? finding.cluster_slug : "";
    const hub = nodes.find((node) => node.id === `c:${slug}`);
    const angle = (index / Math.max(findings.length, 1)) * Math.PI * 2;
    const seat = placed.get(finding.id);
    nodes.push({
      id: finding.id,
      label: finding.title,
      kind: "entry",
      cluster: slug,
      radius: 5 + Math.min(7, (finding.relevance_score ?? 0) / 15),
      // A new entry starts at its theme and walks out from there — which
      // is what makes it look as though the theme grew it.
      x: seat?.x ?? (hub?.x ?? WIDTH / 2) + Math.cos(angle) * 60,
      y: seat?.y ?? (hub?.y ?? HEIGHT / 2) + Math.sin(angle) * 60,
      vx: 0,
      vy: 0,
      score: finding.relevance_score ?? 0,
      depth: 0.5,
    });
    if (hub) edges.push([hub.id, finding.id]);
  });

  const index = new Map(nodes.map((node) => [node.id, node]));
  // A picture that mostly stands only needs to make room for what is new.
  const settled = nodes.filter((node) => placed.has(node.id)).length;
  const newHub = nodes.some((node) => node.kind === "cluster" && !placed.has(node.id));
  const passes =
    newHub || settled <= nodes.length * 0.6
      ? ITERATIONS
      : Math.round(ITERATIONS * 0.25);
  for (let step = 0; step < passes; step += 1) {
    const cooling = 1 - step / passes;

    for (let a = 0; a < nodes.length; a += 1) {
      for (let b = a + 1; b < nodes.length; b += 1) {
        const one = nodes[a]!;
        const two = nodes[b]!;
        let dx = two.x - one.x;
        let dy = two.y - one.y;
        const distance = Math.hypot(dx, dy) || 0.01;
        // Nodes of one theme keep close; strangers push each other away —
        // and two headings hardest of all, because two overlapping cards are
        // unreadable where two overlapping dots are merely close.
        const bothHubs = one.kind === "cluster" && two.kind === "cluster";
        const strength = bothHubs ? 26000 : one.cluster === two.cluster ? 900 : 2600;
        const push = strength / (distance * distance);
        dx /= distance;
        dy /= distance;
        one.vx -= dx * push;
        one.vy -= dy * push;
        two.vx += dx * push;
        two.vy += dy * push;
      }
    }

    for (const [from, to] of edges) {
      const hub = index.get(from)!;
      const leaf = index.get(to)!;
      const dx = leaf.x - hub.x;
      const dy = leaf.y - hub.y;
      const distance = Math.hypot(dx, dy) || 0.01;
      const pull = (distance - 90) * 0.02;
      hub.vx += (dx / distance) * pull;
      hub.vy += (dy / distance) * pull;
      leaf.vx -= (dx / distance) * pull;
      leaf.vy -= (dy / distance) * pull;
    }

    for (const node of nodes) {
      // Gravity keeps the picture on screen without a boundary to bump into.
      node.vx += (WIDTH / 2 - node.x) * (node.kind === "cluster" ? 0.004 : 0.001);
      node.vy += (HEIGHT / 2 - node.y) * (node.kind === "cluster" ? 0.004 : 0.001);
      node.x += Math.max(-24, Math.min(24, node.vx)) * cooling;
      node.y += Math.max(-24, Math.min(24, node.vy)) * cooling;
      node.vx *= 0.55;
      node.vy *= 0.55;
    }
  }

  setDepth(nodes);
  declutter(nodes);
  setDepth(nodes);

  placed.clear();
  for (const node of nodes) placed.set(node.id, { x: node.x, y: node.y });

  return { nodes, edges };
}

/** What sits lower on the plane is nearer. */
function setDepth(nodes: Node[]): void {
  const ys = nodes.map((node) => node.y);
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);
  const span = Math.max(bottom - top, 1);
  for (const node of nodes) node.depth = (node.y - top) / span;
}

/**
 * Push whatever overlaps a heading out of it.
 *
 * The force layout works on the flat plane, but the overlap happens in what
 * is drawn — after the tilt, which compresses y and offsets by depth. So the
 * check runs on the projected positions and the correction is mapped back:
 * a vertical nudge is divided by the tilt, or it would not move far enough.
 */
function declutter(nodes: Node[]): void {
  const hubs = nodes.filter((node) => node.kind === "cluster");
  if (!hubs.length) return;

  for (let pass = 0; pass < 90; pass += 1) {
    for (const hub of hubs) {
      const box = hubBox(hub);
      const anchor = project(hub);
      for (const node of nodes) {
        if (node === hub) continue;
        const point = project(node);
        const own =
          node.kind === "cluster"
            ? hubBox(node)
            : { halfWidth: node.radius, halfHeight: node.radius };
        const halfWidth = (box.halfWidth + own.halfWidth) * anchor.scale + 6;
        const halfHeight = (box.halfHeight + own.halfHeight) * anchor.scale + 6;
        const dx = point.x - anchor.x;
        const dy = point.y - anchor.y;
        if (Math.abs(dx) >= halfWidth || Math.abs(dy) >= halfHeight) continue;

        // Out the near side: the shorter way costs the layout less.
        const outX = halfWidth - Math.abs(dx);
        const outY = halfHeight - Math.abs(dy);
        const share = node.kind === "cluster" ? 0.5 : 1;
        if (outX < outY) {
          const step = (dx >= 0 ? outX : -outX) * share;
          node.x += step;
          if (node.kind === "cluster") hub.x -= step;
        } else {
          const step = ((dy >= 0 ? outY : -outY) / TILT) * share;
          node.y += step;
          if (node.kind === "cluster") hub.y -= step;
        }
      }
    }
  }
}

/** The tilted plane: y is compressed, and nearer rows sink towards the front. */
function project(node: Node): { x: number; y: number; scale: number } {
  const y = HEIGHT / 2 + (node.y - HEIGHT / 2) * TILT + node.depth * 90;
  const scale = 0.72 + node.depth * 0.56;
  return { x: node.x, y, scale };
}

/**
 * The inventory as a map: themes as hubs, entries around them, sized by score.
 *
 * The depth is honest about being a drawing trick — the layout is flat, the
 * plane is tilted and lit from above so the eye can tell foreground from
 * background. That is what makes two hundred dots legible instead of a cloud.
 */
export function ClusterGraph({
  findings,
  clusters,
  onOpen,
  onOpenCluster,
}: {
  findings: Finding[];
  clusters: Cluster[];
  onOpen: (id: string) => void;
  onOpenCluster: (slug: string) => void;
}) {
  const placed = useRef<Placed>(new Map());
  // What was on the plane the last time, so what is new can announce itself.
  const seen = useRef<Set<string> | null>(null);
  const [fresh, setFresh] = useState<Set<string>>(new Set());
  // Was gerade verschwand, noch einen Moment lang sichtbar.
  const [gone, setGone] = useState<Node[]>([]);
  const { nodes, edges } = useMemo(
    () => layout(findings, clusters, placed.current),
    [findings, clusters],
  );
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [selected, setSelected] = useState<string | null>(null);
  const [hovered, setHovered] = useState<Node | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const [screenScale, setScreenScale] = useState(1);
  const moved = useRef(false);
  const frame = useRef<SVGSVGElement>(null);

  // Deliberately not on every change: a redraw mid-conversation must not
  // throw away the pan and zoom the reader chose.
  useEffect(() => setView({ x: 0, y: 0, scale: 1 }), []);

  // Die Knoten des letzten Durchlaufs — daraus wird abgelesen, was ging.
  const previous = useRef<Node[]>([]);

  /**
   * Mark what appeared or left since the last draw, briefly.
   *
   * The first draw marks nothing — everything is new then, and a map that
   * lights up entirely says nothing. That first draw happens on every visit
   * to the tab, not only once per session, which is precisely why it must
   * stay quiet. Afterwards, an entry the assistant adds or removes is the
   * only thing moving, which is exactly the question one has while watching
   * it work.
   */
  useEffect(() => {
    const ids = new Set(nodes.map((node) => node.id));
    const before = seen.current;
    const beforeNodes = previous.current;
    seen.current = ids;
    previous.current = nodes;
    if (before === null) return;

    const appeared = [...ids].filter((id) => !before.has(id));
    const left = beforeNodes.filter((node) => !ids.has(node.id));
    if (!appeared.length && !left.length) return;

    if (appeared.length) setFresh(new Set(appeared));
    if (left.length) setGone(left);
    const timers = [
      window.setTimeout(() => setFresh(new Set()), 2600),
      // Etwas länger als die Ausblendung, sonst springt der Rest weg,
      // bevor er verblasst ist.
      window.setTimeout(() => setGone([]), 600),
    ];
    return () => timers.forEach(window.clearTimeout);
  }, [nodes]);

  const index = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const hues = useMemo(
    // Die Abgehenden zählen mit: Ihr Farbverlauf muss noch da sein, während
    // sie verblassen — auch wenn ihr Cluster mit ihnen verschwunden ist.
    () => [...new Set([...nodes, ...gone].map((node) => node.cluster || UNASSIGNED))],
    [nodes, gone],
  );
  const dimmed = (node: Node) =>
    selected !== null && node.cluster !== selected && node.id !== `c:${selected}`;

  // Painter's algorithm: far first, near last.
  const ordered = useMemo(
    () => [...nodes].sort((one, two) => one.depth - two.depth),
    [nodes],
  );

  // Pointer coordinates must use the SVG viewBox, including letterboxing.
  const pointAt = (clientX: number, clientY: number) => {
    const matrix = frame.current?.getScreenCTM();
    return matrix ? new DOMPoint(clientX, clientY).matrixTransform(matrix.inverse()) : new DOMPoint();
  };

  useEffect(() => {
    const svg = frame.current;
    if (!svg) return;
    const resize = new ResizeObserver(() => setScreenScale(svg.getScreenCTM()?.a ?? 1));
    resize.observe(svg);
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      const point = pointAt(event.clientX, event.clientY);
      setView((current) => {
        const scale = Math.min(6, Math.max(0.4, current.scale * (event.deltaY < 0 ? 1.15 : 1 / 1.15)));
        return { scale, x: point.x - (point.x - current.x) * scale / current.scale, y: point.y - (point.y - current.y) * scale / current.scale };
      });
    };
    svg.addEventListener("wheel", wheel, { passive: false });
    return () => { resize.disconnect(); svg.removeEventListener("wheel", wheel); };
  }, []);

  const zoom = (factor: number) => setView((current) => {
    const scale = Math.min(6, Math.max(0.4, current.scale * factor));
    return { scale, x: WIDTH / 2 - (WIDTH / 2 - current.x) * scale / current.scale, y: HEIGHT / 2 - (HEIGHT / 2 - current.y) * scale / current.scale };
  });

  return (
    <div className="graph-wrap">
      <svg
        ref={frame}
        className="graph-canvas"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
        aria-label="Cluster-Karte"
        onPointerDown={(event) => {
          if (event.pointerType === "mouse" && event.button !== 0) return;
          if (pointers.current.size === 0) moved.current = false;
          pointers.current.set(event.pointerId, pointAt(event.clientX, event.clientY));
          (event.target as Element).setPointerCapture(event.pointerId);
          if (pointers.current.size > 1) moved.current = true;
        }}
        onPointerMove={(event) => {
          if (!pointers.current.has(event.pointerId)) return;
          const before = [...pointers.current.values()];
          const next = pointAt(event.clientX, event.clientY);
          const old = pointers.current.get(event.pointerId)!;
          if (!moved.current && Math.hypot(next.x - old.x, next.y - old.y) * screenScale < 5) return;
          moved.current = true;
          setHovered(null);
          pointers.current.set(event.pointerId, next);
          const after = [...pointers.current.values()];
          const midpoint = (points: { x: number; y: number }[]) => ({
            x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
            y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
          });
          const a = midpoint(before), b = midpoint(after);
          let factor = 1;
          if (before.length === 2) {
            const distance = (points: { x: number; y: number }[]) => Math.hypot(points[0]!.x - points[1]!.x, points[0]!.y - points[1]!.y);
            const previousDistance = distance(before);
            if (previousDistance > 0) factor = distance(after) / previousDistance;
          }
          setView((current) => {
            const scale = Math.min(6, Math.max(0.4, current.scale * factor));
            return { scale, x: b.x - (a.x - current.x) * scale / current.scale, y: b.y - (a.y - current.y) * scale / current.scale };
          });
        }}
        onPointerUp={(event) => pointers.current.delete(event.pointerId)}
        onPointerCancel={(event) => { moved.current = true; pointers.current.delete(event.pointerId); }}
        onLostPointerCapture={(event) => pointers.current.delete(event.pointerId)}
        onClick={() => { if (!moved.current) setSelected(null); }}
        onPointerLeave={() => setHovered(null)}
      >
        <defs>
          {/* A single soft light from above; no gloss, no glow. Depth comes
              from size and from the shadow on the plane, which is enough. */}
          {hues.map((slug) => (
            <linearGradient key={slug} id={`disc-${slug}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={`hsl(${hue(slug)} 52% 62%)`} />
              <stop offset="100%" stopColor={`hsl(${hue(slug)} 52% 46%)`} />
            </linearGradient>
          ))}
        </defs>

        <g transform={`translate(${view.x} ${view.y}) scale(${view.scale})`}>
          {edges.map(([from, to]) => {
            const hub = index.get(from)!;
            const leaf = index.get(to)!;
            const a = project(hub);
            const b = project(leaf);
            // A slight sag, so a hundred straight lines stop looking like a
            // wire fence and start reading as strands.
            const mid = `${(a.x + b.x) / 2} ${(a.y + b.y) / 2 + 14}`;
            return (
              <path
                key={`${from}-${to}`}
                d={`M ${a.x} ${a.y} Q ${mid} ${b.x} ${b.y}`}
                className={"graph-edge" + (dimmed(leaf) ? " dim" : "")}
                style={{
                  ["--chip-hue" as string]: String(hue(hub.cluster)),
                  opacity: dimmed(leaf) ? undefined : 0.18 + leaf.depth * 0.34,
                }}
              />
            );
          })}

          {ordered.map((node) => {
            const point = project(node);
            if (node.kind === "cluster") {
              // A theme is a heading, so it is drawn as one: a card with its
              // name in it, wrapped rather than cut. A bubble would have
              // needed a label beside it anyway, and then the bubble carried
              // nothing.
              const lines = wrap(node.label);
              const box = hubBox(node);
              const width = box.halfWidth * 2 * point.scale;
              const height = box.halfHeight * 2 * point.scale;
              return (
                <g
                  key={node.id}
                  role="button"
                  tabIndex={0}
                  aria-label={`Cluster ${node.label} öffnen`}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault(); setSelected(node.cluster); onOpenCluster(node.cluster);
                    }
                  }}
                  className={
                    "graph-hub" +
                    (dimmed(node) ? " dim" : "") +
                    (fresh.has(node.id) ? " fresh" : "")
                  }
                  style={{ ["--chip-hue" as string]: String(hue(node.cluster)) }}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (moved.current) return;
                    // Both: the map brings the theme forward, the card says
                    // what is in it.
                    setSelected(node.cluster);
                    onOpenCluster(node.cluster);
                  }}
                >
                  <rect
                    x={point.x - width / 2}
                    y={point.y - height / 2}
                    width={width}
                    height={height}
                    rx={7 * point.scale}
                    className={"graph-hub-card" + (selected === node.cluster ? " on" : "")}
                  />
                  <text
                    x={point.x}
                    y={
                      point.y -
                      ((lines.length - 1) * HUB_LINE_HEIGHT * point.scale) / 2 +
                      4 * point.scale
                    }
                    className="graph-hub-label"
                    style={{ fontSize: `${12.5 * point.scale}px` }}
                  >
                    {lines.map((line, row) => (
                      <tspan
                        key={line + row}
                        x={point.x}
                        dy={row === 0 ? 0 : HUB_LINE_HEIGHT * point.scale}
                      >
                        {line}
                      </tspan>
                    ))}
                  </text>
                  <text
                    x={point.x + width / 2 - 2 * point.scale}
                    y={point.y - height / 2 - 5 * point.scale}
                    className="graph-hub-count"
                    style={{ fontSize: `${10 * point.scale}px` }}
                  >
                    {node.score}
                  </text>
                </g>
              );
            }

            const radius = node.radius * point.scale;
            return (
              <g key={node.id} className={dimmed(node) ? "dim" : undefined}>
                <ellipse
                  cx={point.x}
                  cy={point.y + radius * 1.15}
                  rx={radius * 0.95}
                  ry={radius * 0.32}
                  className="graph-shadow"
                />
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={radius}
                  fill={`url(#disc-${node.cluster || UNASSIGNED})`}
                  className={"graph-node entry" + (fresh.has(node.id) ? " fresh" : "")}
                  style={{ ["--chip-hue" as string]: String(hue(node.cluster)) }}
                  pointerEvents="none"
                />
                <circle
                  className="graph-hit"
                  cx={point.x} cy={point.y}
                  r={Math.max(radius, 22 / (screenScale * view.scale))}
                  role="button" tabIndex={0}
                  aria-label={`Eintrag ${node.label} öffnen`}
                  onPointerEnter={(event) => { if (event.pointerType === "mouse") setHovered(node); }}
                  onPointerLeave={() => setHovered(null)}
                  onFocus={() => setHovered(node)}
                  onBlur={() => setHovered(null)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onOpen(node.id); }
                  }}
                  onClick={(event) => { event.stopPropagation(); if (!moved.current) onOpen(node.id); }}
                />
              </g>
            );
          })}

          {/* Was gerade entfernt wurde, verblasst an seinem Platz. Nur
              Umriss und Position — es ist keine Karte mehr, die man liest,
              sondern die Stelle, an der eben noch eine war. */}
          {gone.map((node) => {
            const point = project(node);
            if (node.kind === "cluster") {
              const box = hubBox(node);
              return (
                <rect
                  key={`gone-${node.id}`}
                  className="graph-hub-card graph-gone"
                  x={point.x - box.halfWidth * point.scale}
                  y={point.y - box.halfHeight * point.scale}
                  width={box.halfWidth * 2 * point.scale}
                  height={box.halfHeight * 2 * point.scale}
                  rx={7 * point.scale}
                  style={{ ["--chip-hue" as string]: String(hue(node.cluster)) }}
                />
              );
            }
            return (
              <circle
                key={`gone-${node.id}`}
                className="graph-node entry graph-gone"
                cx={point.x}
                cy={point.y}
                r={node.radius * point.scale}
                fill={`url(#disc-${node.cluster || UNASSIGNED})`}
                style={{ ["--chip-hue" as string]: String(hue(node.cluster)) }}
              />
            );
          })}

          {hovered && (() => {
            const anchor = project(hovered);
            const lift = hovered.radius * anchor.scale;
            const lines = wrap(hovered.label, TIP_LINE, 5);
            const sub =
              hovered.score > 0
                ? `Score ${hovered.score}${
                    hovered.cluster ? ` · ${index.get(`c:${hovered.cluster}`)?.label ?? ""}` : ""
                  }`
                : "";
            const longest = Math.max(...lines.map((line) => line.length), sub.length);
            const width = longest * 7.6 + 30;
            const height = 22 + lines.length * TIP_LINE_HEIGHT + (sub ? 18 : 0);
            return (
              <g
                transform={`translate(${anchor.x} ${anchor.y - lift - 14}) scale(${
                  1 / Math.sqrt(view.scale)
                })`}
                className="graph-tip"
              >
                <rect x={-width / 2} y={-height} width={width} height={height} rx="8" />
                <text className="graph-tip-title">
                  {lines.map((line, row) => (
                    <tspan key={line + row} x="0" y={-height + 24 + row * TIP_LINE_HEIGHT}>
                      {line}
                    </tspan>
                  ))}
                </text>
                {sub && (
                  <text x="0" y={-11} className="graph-tip-sub">
                    {sub}
                  </text>
                )}
              </g>
            );
          })()}

        </g>
      </svg>

      <div className="graph-controls" aria-label="Kartensteuerung">
        <button className="btn secondary" aria-label="Karte vergrößern" onClick={() => zoom(1.3)}>+</button>
        <button className="btn secondary" aria-label="Karte verkleinern" onClick={() => zoom(1 / 1.3)}>−</button>
        <button className="btn secondary" onClick={() => setView({ x: 0, y: 0, scale: 1 })}>Ansicht zurücksetzen</button>
      </div>
      <div className="graph-foot">Ziehen verschiebt, zwei Finger oder +/− zoomen. Antippen öffnet Cluster und Einträge.</div>
    </div>
  );
}
